<template>
  <div id="colorPicker" class="flex flex-col items-start gap-2 self-stretch">
    <UITextSmMedium v-if="typography === 'UITextSmMedium'" class="full-opacity text-gray-700"
      >{{ label }}
    </UITextSmMedium>
    <UITextXsMedium v-else>{{ label }} </UITextXsMedium>
    <div class="color-picker-container" :class="flatRadius ? 'left-zero' : ''">
      <div class="color-picker" :class="compact ? 'compact' : ''">
        <UIColorPicker
          v-if="!shouldShowDefaultIcon || showColorPicker"
          :value="defaultValue"
          :show="shouldShowDefaultIcon ? showColorPicker : undefined"
          type="default"
          @update:value="handleColorPickerInput"
          @on-complete="store.addToHistory"
        />
        <div v-else class="default-color-icon clickable" @click="openColorPicker"></div>
        <div class="color-display">
          <div v-if="isDefaultState" class="default-color-state">
            <UITextSmNormal class="default-text">{{ t("common.default") }}</UITextSmNormal>
          </div>
          <UITextSmNormal v-else>#{{ color }}</UITextSmNormal>
        </div>
        <button
          v-if="clearable && !isDefaultState"
          class="clear-button absolute-clear"
          type="button"
          @click="handleClearColor"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 3L3 9M3 3L9 9"
              stroke="#667085"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { UIColorPicker, UITextSmMedium, UITextSmNormal, UITextXsMedium } from "@gohighlevel/ghl-ui";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const store = useAppStore();

const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  color: {
    type: String,
    default: "FFFFFF",
  },
  compact: {
    type: Boolean,
    default: false,
  },
  flatRadius: {
    type: Boolean,
    default: false,
  },
  typography: {
    type: String,
    default: "UITextXsMedium",
  },
  clearable: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["update:color"]);
const defaultValue = ref("#" + props.color);
const showColorPicker = ref(false);

// Check if we're in default state (empty, null, or "FFFFFF")
const isDefaultState = computed(() => {
  return !props.color || props.color === "" || props.color === "FFFFFF";
});

// Check if we should show the default color icon (only when clearable is true and there's no color value)
const shouldShowDefaultIcon = computed(() => {
  return props.clearable && (!props.color || props.color === "" || props.color === "FFFFFF");
});

watch(
  () => props.color,
  (newColor) => {
    if (isDefaultState.value) {
      defaultValue.value = "#FFFFFF";
    } else {
      defaultValue.value = `#${newColor}`;
    }
  }
);

const openColorPicker = () => {
  defaultValue.value = "#FFFFFF"; // Set a valid default color
  showColorPicker.value = true;
};

const handleColorPickerInput = (value) => {
  const color = value.replace("#", "");
  const isValidColor: boolean = /(^[0-9A-F]{8}$)|(^[0-9A-F]{6}$)|(^[0-9A-F]{3}$)/i.test(color);
  if (!isValidColor) {
    return;
  }
  defaultValue.value = value;
  emit("update:color", color);
  showColorPicker.value = false; // Hide the color picker after selection
};

const handleClearColor = () => {
  emit("update:color", "");
};
</script>

<style scoped>
.color-picker-container {
  position: relative;
  display: flex;
  padding: 7px 12px;
  align-items: center;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #ffffff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
}
.color-picker {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
}
.compact {
  gap: 8px;
}

.color-display {
  display: flex;
  align-items: center;
  flex: 1;
}

.default-color-state {
  display: flex;
  align-items: center;
  gap: 8px;
}

.default-color-icon {
  width: 24px;
  height: 24px;
  background-image: linear-gradient(45deg, #f2f4f7 25%, transparent 25%),
    linear-gradient(-45deg, #f2f4f7 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f2f4f7 75%),
    linear-gradient(-45deg, transparent 75%, #f2f4f7 75%);
  background-size: 6px 6px;
  background-position: 0 0, 0 3px, 3px -3px, -3px 0px;
  border: 1px solid #d0d5dd;
  border-radius: 4px;
  position: relative;
}

.default-color-icon::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 24px;
  height: 2px;
  background-color: #f04438;
  transform: translate(-50%, -50%) rotate(45deg);
  border-radius: 1.5px;
}

.default-color-icon.clickable {
  cursor: pointer;
  transition: opacity 0.2s;
}

.default-color-icon.clickable:hover {
  opacity: 0.8;
}

.default-text {
  color: #667085;
}

.clear-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  border-radius: 2px;
  transition: background-color 0.2s;
}

.clear-button:hover {
  background-color: #f2f4f7;
}

.absolute-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}

#colorPicker .n-color-picker {
  width: 24px;
  height: 24px;
}
.v-binder-follower-content {
  z-index: 11000 !important;
}

.left-zero {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left-width: 0px !important;
}

.full-opacity {
  --tw-text-opacity: 1;
}
</style>
